/**
 * Centralized environment configuration for Admin Dashboard
 * Provides typed access to environment variables with validation
 */

class AdminEnvironmentConfig {
  constructor() {
    this.config = this.buildConfig();
    this.validateConfig();
  }

  buildConfig() {
    return {
      // API Configuration
      api: {
        baseUrl: this.getApiBaseUrl(),
        timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
        retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS) || 3
      },

      // Application Configuration
      app: {
        name: 'NYCRiddles Admin Dashboard',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
        environment: import.meta.env.MODE || 'development',
        isDevelopment: import.meta.env.MODE === 'development',
        isProduction: import.meta.env.MODE === 'production'
      },

      // Feature Flags
      features: {
        enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
        enableDebugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
        enableOfflineMode: import.meta.env.VITE_OFFLINE_MODE === 'true'
      },

      // UI Configuration
      ui: {
        refreshInterval: parseInt(import.meta.env.VITE_REFRESH_INTERVAL) || 30000,
        maxFileSize: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 10485760, // 10MB
        itemsPerPage: parseInt(import.meta.env.VITE_ITEMS_PER_PAGE) || 25
      }
    };
  }

  getApiBaseUrl() {
    // Priority order for API URL resolution
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }
    
    if (import.meta.env.RAILWAY_PUBLIC_DOMAIN) {
      return `https://${import.meta.env.RAILWAY_PUBLIC_DOMAIN}`;
    }
    
    // Default to localhost for development
    return 'http://localhost:3001';
  }

  validateConfig() {
    const errors = [];

    // Validate API URL format
    try {
      new URL(this.config.api.baseUrl);
    } catch {
      errors.push('Invalid API base URL format');
    }

    // Validate numeric values
    if (isNaN(this.config.api.timeout) || this.config.api.timeout <= 0) {
      errors.push('API timeout must be a positive number');
    }

    if (isNaN(this.config.ui.refreshInterval) || this.config.ui.refreshInterval <= 0) {
      errors.push('Refresh interval must be a positive number');
    }

    if (errors.length > 0) {
      console.error('Environment configuration errors:', errors);
      throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
    }
  }

  // Getters for easy access
  get api() { return this.config.api; }
  get app() { return this.config.app; }
  get features() { return this.config.features; }
  get ui() { return this.config.ui; }

  // Utility methods
  isFeatureEnabled(feature) {
    return this.config.features[feature] === true;
  }

  getApiEndpoint(endpoint) {
    return `${this.config.api.baseUrl}${endpoint}`;
  }

  // Get configuration summary for debugging
  getConfigSummary() {
    return {
      api: {
        baseUrl: this.config.api.baseUrl,
        timeout: this.config.api.timeout
      },
      app: {
        name: this.config.app.name,
        version: this.config.app.version,
        environment: this.config.app.environment
      },
      features: this.config.features,
      ui: {
        refreshInterval: this.config.ui.refreshInterval,
        itemsPerPage: this.config.ui.itemsPerPage
      }
    };
  }
}

// Create singleton instance
const adminEnvConfig = new AdminEnvironmentConfig();

export default adminEnvConfig;
